import React, { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import type { Database } from '../lib/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

const buildFallbackProfile = (sessionUser: any): Profile => ({
  id: sessionUser.id,
  user_id: sessionUser.id,
  full_name: sessionUser.user_metadata?.full_name || null,
  name: sessionUser.user_metadata?.name || null,
  email: sessionUser.email || null,
  avatar_url: sessionUser.user_metadata?.avatar_url || null,
  company_name: sessionUser.user_metadata?.company_name || null,
  subscription_tier: 'free',
  subscription_ends_at: null,
  created_at: sessionUser.created_at,
  updated_at: sessionUser.last_sign_in_at || sessionUser.created_at,
} as Profile);

interface Props {
  children: React.ReactNode;
}

export default function AuthInitializer({ children }: Props) {
  const setUser = useAuthStore((state) => state.setUser);
  const clearUser = useAuthStore((state) => state.clearUser);

  useEffect(() => {
    let cancelled = false;
    console.log('[AuthInitializer] mount: starting auth hydration');

    // Safety net: if getSession or profile lookup hangs, force-resolve auth
    // state to "logged out" so the login UI doesn't get permanently disabled
    // by a stuck loading flag.
    const safetyTimeout = setTimeout(() => {
      if (cancelled) return;
      console.warn('[AuthInitializer] safety timeout fired — forcing clearUser');
      clearUser();
    }, 5000);

    const hydrateFromSession = async (session: any) => {
      if (!session?.user) {
        console.log('[AuthInitializer] hydrate: no session/user, clearing');
        clearUser();
        return;
      }

      console.log('[AuthInitializer] hydrate: session found', {
        userId: session.user.id,
        email: session.user.email,
        expiresAt: session.expires_at,
      });

      // Authenticate immediately with a fallback profile so the UI never
      // gets stuck waiting on a slow/hung `profiles` row. The profile
      // lookup runs in the background and enriches the store on success.
      // Cancel the safety timeout — auth state is now resolved regardless
      // of whether getSession's .then() ever fires.
      clearTimeout(safetyTimeout);
      setUser(buildFallbackProfile(session.user), session.access_token);

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (cancelled) return;

        if (profile && !error) {
          console.log('[AuthInitializer] hydrate: profile loaded, enriching user');
          setUser(
            {
              ...profile,
              email: session.user.email || profile.email || null,
            },
            session.access_token
          );
        } else {
          console.warn('[AuthInitializer] hydrate: profile lookup returned no row', { error });
        }
      } catch (err) {
        console.error('[AuthInitializer] hydrate: profile fetch threw', err);
      }
    };

    console.log('[AuthInitializer] calling supabase.auth.getSession()');
    supabase.auth
      .getSession()
      .then(({ data: { session } }: any) => {
        if (cancelled) return;
        console.log('[AuthInitializer] getSession resolved', { hasSession: !!session });
        clearTimeout(safetyTimeout);
        hydrateFromSession(session);
      })
      .catch((err: unknown) => {
        console.error('[AuthInitializer] getSession failed', err);
        if (!cancelled) {
          clearTimeout(safetyTimeout);
          clearUser();
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: any) => {
      if (cancelled) return;

      // CRITICAL: never `await` async work inside this callback. Supabase v2
      // holds the auth lock while running listeners and only resolves the
      // originating auth call (signInWithPassword, updateUser, etc.) AFTER
      // listeners return. An await here that touches the database or calls
      // any auth method deadlocks the originating call — symptom: forms
      // that successfully save server-side but leave their "Saving…" button
      // frozen because the await never resolves. Defer all hydration work
      // via setTimeout(0) so the callback returns synchronously.

      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        clearUser();
        return;
      }

      if (
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED'
      ) {
        if (session?.user) {
          setTimeout(() => {
            if (cancelled) return;
            hydrateFromSession(session);
          }, 0);
        } else {
          clearUser();
        }
        return;
      }

      // INITIAL_SESSION fires on subscribe; getSession above already handled it.
      // For any other event, only refresh if a session is actually present —
      // never auto-redirect here. Route guards decide where the user goes.
      if (session?.user) {
        setTimeout(() => {
          if (cancelled) return;
          hydrateFromSession(session);
        }, 0);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(safetyTimeout);
      subscription?.unsubscribe?.();
    };
  }, [setUser, clearUser]);

  return <>{children}</>;
}
